import React, { useEffect, useMemo, useRef, useState } from 'react';
import { CalendarDays, ChevronLeft, ChevronRight } from 'lucide-react';

const WEEKDAYS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

const toLocalDate = (isoDate) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(String(isoDate || ''))) return null;
  const [year, month, day] = isoDate.split('-').map(Number);
  return new Date(year, month - 1, day);
};

const formatDate = (isoDate) => {
  const date = toLocalDate(isoDate);
  return date ? new Intl.DateTimeFormat('pt-BR').format(date) : '';
};

const getInitialMonth = (value, min) => {
  const date = toLocalDate(value) || toLocalDate(min) || new Date();
  return new Date(date.getFullYear(), date.getMonth(), 1);
};

const buildCalendarCells = (monthDate) => {
  const year = monthDate.getFullYear();
  const month = monthDate.getMonth();
  const firstWeekday = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = Array.from({ length: firstWeekday }, () => null);

  for (let day = 1; day <= daysInMonth; day += 1) {
    cells.push({
      day,
      value: `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`,
    });
  }

  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
};

export default function DarkDatePicker({
  id,
  value = '',
  onChange,
  min = '',
  disabled = false,
  placeholder = 'dd/mm/aaaa',
}) {
  const wrapperRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [displayMonth, setDisplayMonth] = useState(() => getInitialMonth(value, min));
  const cells = useMemo(() => buildCalendarCells(displayMonth), [displayMonth]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const closeOnOutsideClick = (event) => {
      if (!wrapperRef.current?.contains(event.target)) setIsOpen(false);
    };
    const closeOnEscape = (event) => {
      if (event.key === 'Escape') setIsOpen(false);
    };

    document.addEventListener('mousedown', closeOnOutsideClick);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('mousedown', closeOnOutsideClick);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [isOpen]);

  const openCalendar = () => {
    if (disabled) return;
    setDisplayMonth(getInitialMonth(value, min));
    setIsOpen(true);
  };

  const selectDate = (nextValue) => {
    onChange(nextValue);
    setIsOpen(false);
  };

  return (
    <div ref={wrapperRef} className="relative">
      <button
        id={id}
        type="button"
        aria-label={`Selecionar data${value ? `: ${formatDate(value)}` : ''}`}
        aria-expanded={isOpen}
        onClick={openCalendar}
        disabled={disabled}
        className="flex w-full items-center justify-between rounded-lg border border-slate-800 bg-slate-950 px-3 py-2 text-left text-sm outline-none transition hover:border-slate-700 focus:border-orange-500 disabled:cursor-not-allowed disabled:opacity-60"
      >
        <span className={value ? 'text-white' : 'text-slate-400'}>{formatDate(value) || placeholder}</span>
        <CalendarDays size={16} className="text-slate-500" />
      </button>

      {isOpen && (
        <div className="absolute left-0 top-full z-[140] mt-2 w-[320px] rounded-xl border border-slate-700 bg-slate-950 p-4 shadow-2xl shadow-black/60">
          <div className="mb-3 flex items-center justify-between">
            <button
              type="button"
              aria-label="Mês anterior"
              onClick={() => setDisplayMonth((current) => new Date(current.getFullYear(), current.getMonth() - 1, 1))}
              className="flex h-8 w-8 items-center justify-center rounded-lg border border-slate-700 text-slate-300 transition hover:border-slate-500 hover:text-white"
            >
              <ChevronLeft size={16} />
            </button>
            <span className="text-sm font-bold capitalize text-white">
              {new Intl.DateTimeFormat('pt-BR', { month: 'long', year: 'numeric' }).format(displayMonth)}
            </span>
            <button
              type="button"
              aria-label="Próximo mês"
              onClick={() => setDisplayMonth((current) => new Date(current.getFullYear(), current.getMonth() + 1, 1))}
              className="flex h-8 w-8 items-center justify-center rounded-lg border border-slate-700 text-slate-300 transition hover:border-slate-500 hover:text-white"
            >
              <ChevronRight size={16} />
            </button>
          </div>

          <div className="mb-2 grid grid-cols-7 gap-1">
            {WEEKDAYS.map((weekday) => (
              <span key={weekday} className="py-1 text-center text-[11px] font-bold uppercase text-slate-500">{weekday}</span>
            ))}
          </div>

          <div className="grid grid-cols-7 gap-1">
            {cells.map((cell, index) => {
              if (!cell) return <div key={`empty-${index}`} className="h-9" />;
              const isSelected = cell.value === value;
              const isUnavailable = Boolean(min && cell.value < min);

              return (
                <button
                  key={cell.value}
                  type="button"
                  disabled={isUnavailable}
                  onClick={() => selectDate(cell.value)}
                  className={`h-9 rounded-lg text-sm font-semibold transition ${
                    isSelected
                      ? 'border border-orange-500 bg-orange-500/15 text-orange-300'
                      : 'border border-transparent text-slate-200 hover:border-slate-700 hover:bg-slate-900'
                  } ${isUnavailable ? 'cursor-not-allowed text-slate-700 hover:border-transparent hover:bg-transparent' : ''}`}
                >
                  {cell.day}
                </button>
              );
            })}
          </div>

          {value && (
            <button
              type="button"
              onClick={() => selectDate('')}
              className="mt-3 text-xs font-semibold text-slate-400 transition hover:text-white"
            >
              Limpar data
            </button>
          )}
        </div>
      )}
    </div>
  );
}
